import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CNPJ_MASK, CNPJ_SIZE, CPF_MASK, CPF_SIZE } from '@shared/constants';
import { isPhoneNumber, PHONE_LANDLINE_MASK, PHONE_MOBILE_MASK } from '@shared/functions/format-document';
import { MatTooltip } from '@angular/material/tooltip';
import { NgxMaskPipe } from 'ngx-mask';
import { EmptyValuePipe, TruncatePipe } from '@shared/pipes';

const AVATAR_PALETTE = [
  '#235e91',
  '#33a033',
  '#ff921b',
  '#8b5cf6',
  '#1998a7',
  '#f53948',
  '#8548ae',
  '#f59e0b',
];

@Component({
    selector: 'app-identification-column',
    templateUrl: './identification-column.component.html',
    styleUrls: ['./identification-column.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatTooltip, NgxMaskPipe, EmptyValuePipe, TruncatePipe]
})
export class IdentificationColumnComponent {
  readonly name = input<string>('');
  readonly documentLabel = input<string>('');
  readonly documentNumber = input<string>('');
  readonly imageUrl = input<string | null | undefined>(null);
  readonly showAvatar = input<boolean>(false);

  readonly PHONE_MOBILE_MASK = PHONE_MOBILE_MASK;
  readonly PHONE_LANDLINE_MASK = PHONE_LANDLINE_MASK;
  readonly CNPJ_MASK = CNPJ_MASK;
  readonly CPF_MASK = CPF_MASK;

  readonly initials = computed(() => {
    const fullName = (this.name() ?? '').trim();
    if (!fullName) return '';
    const parts = fullName.split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  });

  readonly avatarColor = computed(() => {
    const fullName = (this.name() ?? '').trim();
    if (!fullName) return AVATAR_PALETTE[0];
    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = (hash * 31 + fullName.charCodeAt(i)) | 0;
    }
    const idx = Math.abs(hash) % AVATAR_PALETTE.length;
    return AVATAR_PALETTE[idx];
  });

  get documentMask(): string {
    const raw = this.documentNumber();
    if (!raw) return '';

    const digits = raw.replace(/\D/g, '');

    if (isPhoneNumber(raw)) {
      return digits.length === 10 ? this.PHONE_LANDLINE_MASK : this.PHONE_MOBILE_MASK;
    }

    // CNPJ pode ser alfanumérico (RFB jul/2026): conta letras + dígitos para
    // distinguir de CPF (11 dígitos numéricos).
    const alnum = raw.toUpperCase().replace(/[^0-9A-Z]/g, '');

    return alnum.length > CPF_SIZE || alnum.length === CNPJ_SIZE
      ? this.CNPJ_MASK
      : this.CPF_MASK;
  }

}
